import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to set up SSO into AWS with SAML',
    metaDescription:
      'Federate AWS Identity Center (or IAM federation) with Monosign via SAML 2. Four-step walkthrough with deep-link to the canonical docs.',
    heroEyebrow: 'How-to · SSO into AWS',
    heroTitle: ['AWS SSO via SAML.', 'Four steps.'],
    heroBody:
      'Federate AWS (Identity Center or legacy IAM) with Monosign in under fifteen minutes. Walk through the four steps below, then jump to the full guide for screenshots and version-specific notes.',
    badges: ['SAML 2.0', 'AWS Identity Center + IAM', '~6 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From zero to AWS console sign-in.',
    stepsBody: 'The high-level shape. The docs have the every-button screenshots.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Register AWS as a SAML service provider in Monosign',
        body: "In the Monosign admin console, create a new SAML provider for AWS. Set the audience URI to urn:amazon:webservices and download Monosign's SAML metadata file; you will hand it to AWS in the next step.",
        hint: 'Use Identity Center (the modern AWS SSO) for new tenants; the legacy IAM federation path still works but is in maintenance mode.',
      },
      {
        title: 'Configure AWS to trust Monosign as an IdP',
        body: "In AWS IAM Identity Center (or IAM > Identity providers for IAM federation), import Monosign's metadata XML. AWS creates the trust relationship and the role mapping surface.",
      },
      {
        title: 'Map roles to Monosign groups',
        body: 'In AWS, attach a SAML role and define which Monosign groups are allowed to assume it. In Monosign, add the SAML attribute mappings (Role, RoleSessionName) that AWS expects in the assertion.',
        hint: 'Stick to one Monosign group per AWS role to keep the matrix readable as your account count grows.',
      },
      {
        title: 'Test the round-trip',
        body: 'Sign in as a test user, click the AWS tile in Monosign, and confirm the assertion lands you in the AWS console with the expected permissions. Roll out to broader groups once the test user works end-to-end.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Integrations catalog', description: 'Every Monofor integration in one place.', href: '/integrations' },
      { label: 'Single Sign-On', description: 'The SSO capability behind every SAML setup.', href: '/sign/sso' },
      { label: 'For Developers', description: 'OAuth 2, OIDC, SAML, JWT primer.', href: '/for/developers' },
    ],
  },
  tr: {
    metaTitle: "SAML ile AWS'ye SSO nasıl kurulur",
    metaDescription:
      "AWS Identity Center'ı (veya IAM federasyonunu) SAML 2 ile Monosign'a bağlayın. Dört adımlık rehber ve kapsamlı dokümana derin bağlantı.",
    heroEyebrow: "Rehber · AWS'ye SSO",
    heroTitle: ['SAML ile AWS SSO.', 'Dört adım.'],
    heroBody:
      "AWS'yi (Identity Center veya eski IAM) on beş dakikadan kısa sürede Monosign ile federasyona alın. Aşağıdaki dört adımı izleyin; ekran görüntüleri ve sürüme özel notlar için tam rehbere geçin.",
    badges: ['SAML 2.0', 'AWS Identity Center + IAM', '~6 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Sıfırdan AWS konsolu girişine.',
    stepsBody: 'Genel hatlarıyla akış. Her düğmenin ekran görüntüsü dokümanlarda.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "AWS'yi Monosign'da SAML servis sağlayıcısı olarak kaydedin",
        body: "Monosign yönetim konsolunda AWS için yeni bir SAML sağlayıcısı oluşturun. Audience URI değerini urn:amazon:webservices olarak ayarlayın ve Monosign'ın SAML metadata dosyasını indirin; bir sonraki adımda AWS'ye vereceksiniz.",
        hint: "Yeni kurulumlar için Identity Center'ı (modern AWS SSO) kullanın; eski IAM federasyon yolu hâlâ çalışır ancak bakım modundadır.",
      },
      {
        title: "AWS'yi Monosign'a IdP olarak güvenecek şekilde yapılandırın",
        body: "AWS IAM Identity Center'da (IAM federasyonu için IAM > Identity providers) Monosign'ın metadata XML dosyasını içe aktarın. AWS güven ilişkisini ve rol eşleme yüzeyini oluşturur.",
      },
      {
        title: 'Rolleri Monosign gruplarına eşleyin',
        body: "AWS tarafında bir SAML rolü ekleyin ve bu rolü hangi Monosign gruplarının üstlenebileceğini tanımlayın. Monosign tarafında, AWS'nin assertion içinde beklediği SAML öznitelik eşlemelerini (Role, RoleSessionName) ekleyin.",
        hint: 'Hesap sayınız arttıkça matrisin okunabilir kalması için her AWS rolüne tek bir Monosign grubu bağlayın.',
      },
      {
        title: 'Uçtan uca test edin',
        body: "Bir test kullanıcısıyla oturum açın, Monosign'daki AWS kutucuğuna tıklayın ve assertion'ın sizi beklenen yetkilerle AWS konsoluna götürdüğünü doğrulayın. Test kullanıcısı uçtan uca çalıştıktan sonra daha geniş gruplara yayın.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Entegrasyon kataloğu', description: 'Tüm Monofor entegrasyonları tek yerde.', href: '/integrations' },
      { label: 'Tek Oturum Açma (SSO)', description: 'Her SAML kurulumunun arkasındaki SSO yeteneği.', href: '/sign/sso' },
      { label: 'Geliştiriciler için', description: 'OAuth 2, OIDC, SAML, JWT temelleri.', href: '/for/developers' },
    ],
  },
}

type Props = { locale: string }

export default function SsoAwsSamlPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/sso-aws-saml"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/aws-saml-integration' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/aws-saml-integration"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
